import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Check, Plus, Save, Trash2, X } from "lucide-react";
import { TrainingVideoButton } from "./TrainingVideo";
import { Card, Eyebrow, Kpi, NumField, btnDark, btnLight, monthLabel } from "./ui";

const PLANS = [
  ["minimum", "Minimums only", "#64748B"],
  ["avalanche", "Avalanche (highest rate first)", "#0F172A"],
  ["snowball", "Snowball (smallest balance first)", "#0891B2"],
];
const EMPTY = { name: "", kind: "loan", balance: "", apr: "", min_payment: "" };
const cell = "px-2 py-1.5 border text-sm font-mono-fin w-full bg-white";

export default function DebtTab({ ov, reload }) {
  const plan = ov.debt_plan;
  const [extra, setExtra] = useState(ov.settings.extra_debt_payment);
  const [sugg, setSugg] = useState([]);
  const [form, setForm] = useState(EMPTY);

  const loadSugg = async () => {
    try { setSugg((await api.get("/advisory/debts/suggestions")).data || []); } catch { setSugg([]); }
  };
  useEffect(() => { loadSugg(); }, [ov.debts.length]);

  const add = async (d) => {
    try {
      await api.post("/advisory/debts", {
        name: d.name, kind: d.kind, balance: Number(d.balance) || 0, apr: Number(d.apr) || 0,
        min_payment: Number(d.min_payment) || 0, source_key: d.source_key,
      });
      toast.success(`Added ${d.name}`);
      setForm(EMPTY);
      await reload();
    } catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Add failed"); }
  };
  const patch = async (id, k, v) => {
    try { await api.patch(`/advisory/debts/${id}`, { [k]: k === "name" ? v : Number(v) || 0 }); await reload(); }
    catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || "Update failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this debt?")) return;
    try { await api.delete(`/advisory/debts/${id}`); await reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };
  const dismiss = async (k) => {
    try {
      await api.post("/advisory/debts/suggestions/dismiss", { source_key: k });
      setSugg((s) => s.filter((x) => x.source_key !== k));
    } catch (err) { toast.error(err?.response?.data?.detail || "Dismiss failed"); }
  };
  const saveExtra = async () => {
    try {
      await api.put("/advisory/settings", { extra_debt_payment: Number(extra) || 0 });
      toast.success("Extra payment saved");
      await reload();
    } catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || "Save failed"); }
  };
  const rec = plan.plans?.[plan.recommended];

  return (
    <div className="space-y-5">
      <div className="flex justify-end -mb-2"><TrainingVideoButton video="debt" /></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="debt-kpi-total" label="Total debt" value={fmtMoney(plan.total_balance)} tone={plan.total_balance > 0 ? "bad" : undefined} />
        <Kpi testid="debt-kpi-min" label="Monthly minimums" value={fmtMoney(plan.total_min_payment)} sub={`Weighted APR ${plan.weighted_apr || 0}%`} />
        <Kpi testid="debt-kpi-free" label="Debt-free (recommended)" value={rec?.debt_free_month ? monthLabel(rec.debt_free_month) : "—"} sub={rec ? `${plan.recommended} · ${rec.months} months` : "Add debts below"} tone="good" />
        <Kpi testid="debt-kpi-saved" label="Interest saved vs minimums" value={rec?.interest_saved != null ? fmtMoney(rec.interest_saved) : "—"} tone="good" sub={`With ${fmtMoney(plan.extra_payment)}/mo extra${plan.extra_from_goals ? ` (incl. ${fmtMoney(plan.extra_from_goals)} from goal plans)` : ""}`} />
      </div>
      {sugg.length > 0 && (
        <Card testid="debt-suggestions-card">
          <Eyebrow>Detected from your data</Eyebrow>
          <div className="font-display font-bold text-lg mb-3">Possible debts — confirm to include</div>
          <ul className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
            {sugg.map((s) => (
              <li key={s.source_key} className="py-2.5 flex flex-wrap items-center justify-between gap-3" data-testid={`debt-suggestion-${s.source_key}`}>
                <div>
                  <div className="text-sm font-medium">{s.name} <span className="font-mono-fin text-xs ml-2">{fmtMoney(s.balance)} · {s.apr}% · min {fmtMoney(s.min_payment)}</span></div>
                  <div className="text-[11px] text-[var(--ll-ink-soft)]">{s.why}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => add(s)} className={btnDark} data-testid={`debt-suggestion-add-${s.source_key}`}><Check size={13} /> Add</button>
                  <button onClick={() => dismiss(s.source_key)} className={btnLight} style={{ borderColor: "var(--ll-border)" }} data-testid={`debt-suggestion-dismiss-${s.source_key}`}><X size={13} /> Dismiss</button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card testid="debt-list-card">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div><Eyebrow>Your debts</Eyebrow><div className="font-display font-bold text-lg">Balances, rates & minimums</div></div>
          <div className="flex items-end gap-2">
            <div className="w-44"><NumField label="Extra payment / month" suffix="$" step="50" min="0" value={extra} onChange={setExtra} testid="debt-extra-input" /></div>
            <button onClick={saveExtra} className={btnDark} data-testid="debt-extra-save"><Save size={14} /> Apply</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="debt-table">
            <thead><tr className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] text-left">
              <th className="py-2 pr-2">Name</th><th className="pr-2">Balance</th><th className="pr-2">APR %</th><th className="pr-2">Min / mo</th><th />
            </tr></thead>
            <tbody>
              {ov.debts.map((d) => (
                <tr key={d.id} data-testid={`debt-row-${d.id}`}>
                  <td className="py-1 pr-2"><input defaultValue={d.name} onBlur={(e) => e.target.value !== d.name && patch(d.id, "name", e.target.value)} className={`${cell} font-sans`} style={{ borderColor: "var(--ll-border)" }} data-testid={`debt-name-${d.id}`} /></td>
                  {["balance", "apr", "min_payment"].map((k) => (
                    <td key={k} className="pr-2"><input type="number" defaultValue={d[k]} onBlur={(e) => Number(e.target.value) !== d[k] && patch(d.id, k, e.target.value)} className={cell} style={{ borderColor: "var(--ll-border)" }} data-testid={`debt-${k}-${d.id}`} /></td>
                  ))}
                  <td><button onClick={() => del(d.id)} className="p-2 border hover:bg-[#FEE2E2]" style={{ borderColor: "var(--ll-border)" }} data-testid={`debt-delete-${d.id}`}><Trash2 size={13} /></button></td>
                </tr>
              ))}
              <tr data-testid="debt-add-row">
                <td className="py-1 pr-2"><input placeholder="New debt name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${cell} font-sans`} style={{ borderColor: "var(--ll-border)" }} data-testid="debt-add-name" /></td>
                {["balance", "apr", "min_payment"].map((k) => (
                  <td key={k} className="pr-2"><input type="number" placeholder="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={cell} style={{ borderColor: "var(--ll-border)" }} data-testid={`debt-add-${k}`} /></td>
                ))}
                <td><button onClick={() => form.name.trim() && add(form)} disabled={!form.name.trim()} className="p-2 text-white bg-[var(--ll-ink)] disabled:opacity-40" data-testid="debt-add-btn"><Plus size={13} /></button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
      {plan.plans?.minimum && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="debt-plans">
          {PLANS.map(([k, label, color]) => {
            const p = plan.plans[k];
            return (
              <Card key={k} testid={`debt-plan-${k}`} className="relative">
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />
                <div className="flex items-center justify-between"><Eyebrow>{label}</Eyebrow>{plan.recommended === k && <span className="text-[10px] uppercase tracking-widest text-[var(--ll-success)] font-bold">Recommended</span>}</div>
                <div className="font-display font-bold text-xl mt-2">{p.stuck ? "Never (payments < interest)" : monthLabel(p.debt_free_month)}</div>
                <div className="text-xs text-[var(--ll-ink-soft)] mt-1 font-mono-fin">{p.months ? `${p.months} months` : ""} · interest {fmtMoney(p.total_interest)}</div>
                {p.interest_saved != null && <div className="text-sm font-mono-fin font-bold text-[var(--ll-success)] mt-1">Saves {fmtMoney(p.interest_saved)}</div>}
                <ol className="mt-3 space-y-1 text-xs">
                  {p.order.map((o, i) => (
                    <li key={o.id} className="flex justify-between"><span>{i + 1}. {o.name}</span><span className="font-mono-fin">{monthLabel(o.payoff_date)}</span></li>
                  ))}
                </ol>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
